import type {
  PageSpecificAboutUsInfoComponent,
  PageSpecificBlogListComponent,
  PageSpecificBoardProfilesComponent,
  PageSpecificCashbackComponent,
  PageSpecificContactChannelsComponent,
  PageSpecificContactComponent,
  PageSpecificContactInfoComponent,
  PageSpecificFeatureComponent,
  PageSpecificHtmlEmbedComponent,
  PageSpecificInvoiceBannerComponent,
  PageSpecificMediaArticleContactComponent,
  PageSpecificMediaArticleListComponent,
  PageSpecificTwistoAccountComponent,
  PageSpecificTypeformComponent,
  PageSpecificVideoStoriesComponent,
  SectionsAboutUsInfoComponent,
  SectionsBlogListComponent,
  SectionsBoardProfilesComponent,
  SectionsCashbackComponent,
  SectionsContactChannelsComponent,
  SectionsContactComponent,
  SectionsContactInfoComponent,
  SectionsFeatureComponent,
  SectionsHtmlEmbedComponent,
  SectionsInvoiceBannerComponent,
  SectionsMediaArticleContactComponent,
  SectionsMediaArticleListComponent,
  SectionsTwistoAccountComponent,
  SectionsTypeformComponent,
  SectionsVideoStoriesComponent,
} from '@twisto/api-cms'

import {
  AboutUsInfo,
  BlogList,
  BoardProfileInfo,
  Cashback,
  Contact,
  ContactChannels,
  ContactInfo,
  Feature,
  HTMLEmbed,
  InvoiceBanner,
  MediaArticleContact,
  MediaArticlesList,
  TwistoAccount,
  Typeform,
  VideoStories,
} from '../sections'

export const COMPONENTS_TYPE = {
  PAGE_SPECIFIC_TWISTO_ACCOUNT: 'page-specific.twisto-account',
  PAGE_SPECIFIC_INVOICE_BANNER: 'page-specific.invoice-banner',
  PAGE_SPECIFIC_CASHBACK: 'page-specific.cashback',
  PAGE_SPECIFIC_TYPEFORM: 'page-specific.typeform',
  PAGE_SPECIFIC_CONTACT_INFO: 'page-specific.contact-info',
  PAGE_SPECIFIC_CONTACT: 'page-specific.contact',
  PAGE_SPECIFIC_CONTACT_CHANNELS: 'page-specific.contact-channels',
  PAGE_SPECIFIC_ABOUT_US_INFO: 'page-specific.about-us-info',
  PAGE_SPECIFIC_BOARD_PROFILES: 'page-specific.board-profiles',
  PAGE_SPECIFIC_VIDEO_STORIES: 'page-specific.video-stories',
  PAGE_SPECIFIC_BLOG_LIST: 'page-specific.blog-list',
  PAGE_SPECIFIC_MEDIA_ARTICLE_LIST: 'page-specific.media-article-list',
  PAGE_SPECIFIC_MEDIA_ARTICLE_CONTACT: 'page-specific.media-article-contact',
  PAGE_SPECIFIC_HTML_EMBED: 'page-specific.html-embed',
  PAGE_SPECIFIC_FEATURE: 'page-specific.feature',
} as const

export type PageSpecificType =
  | (PageSpecificTwistoAccountComponent & {
      __component: (typeof COMPONENTS_TYPE)['PAGE_SPECIFIC_TWISTO_ACCOUNT']
    })
  | (PageSpecificInvoiceBannerComponent & {
      __component: (typeof COMPONENTS_TYPE)['PAGE_SPECIFIC_INVOICE_BANNER']
    })
  | (PageSpecificCashbackComponent & {
      __component: (typeof COMPONENTS_TYPE)['PAGE_SPECIFIC_CASHBACK']
    })
  | (PageSpecificTypeformComponent & {
      __component: (typeof COMPONENTS_TYPE)['PAGE_SPECIFIC_TYPEFORM']
    })
  | (PageSpecificContactInfoComponent & {
      __component: (typeof COMPONENTS_TYPE)['PAGE_SPECIFIC_CONTACT_INFO']
    })
  | (PageSpecificContactComponent & {
      __component: (typeof COMPONENTS_TYPE)['PAGE_SPECIFIC_CONTACT']
    })
  | (PageSpecificContactChannelsComponent & {
      __component: (typeof COMPONENTS_TYPE)['PAGE_SPECIFIC_CONTACT_CHANNELS']
    })
  | (PageSpecificAboutUsInfoComponent & {
      __component: (typeof COMPONENTS_TYPE)['PAGE_SPECIFIC_ABOUT_US_INFO']
    })
  | (PageSpecificBoardProfilesComponent & {
      __component: (typeof COMPONENTS_TYPE)['PAGE_SPECIFIC_BOARD_PROFILES']
    })
  | (PageSpecificVideoStoriesComponent & {
      __component: (typeof COMPONENTS_TYPE)['PAGE_SPECIFIC_VIDEO_STORIES']
    })
  | (PageSpecificBlogListComponent & {
      __component: (typeof COMPONENTS_TYPE)['PAGE_SPECIFIC_BLOG_LIST']
    })
  | (PageSpecificMediaArticleListComponent & {
      __component: (typeof COMPONENTS_TYPE)['PAGE_SPECIFIC_MEDIA_ARTICLE_LIST']
    })
  | (PageSpecificMediaArticleContactComponent & {
      __component: (typeof COMPONENTS_TYPE)['PAGE_SPECIFIC_MEDIA_ARTICLE_CONTACT']
    })
  | (PageSpecificHtmlEmbedComponent & {
      __component: (typeof COMPONENTS_TYPE)['PAGE_SPECIFIC_HTML_EMBED']
    })
  | (PageSpecificFeatureComponent & {
      __component: (typeof COMPONENTS_TYPE)['PAGE_SPECIFIC_FEATURE']
    })

type PageSpecificProps = {
  pageSpecific?: PageSpecificType[]
}

export const PageSpecific = ({
  pageSpecificData,
}: {
  pageSpecificData: PageSpecificType
}) => {
  switch (pageSpecificData.__component) {
    case 'page-specific.twisto-account':
      return (
        <TwistoAccount
          {...(pageSpecificData as unknown as SectionsTwistoAccountComponent)}
        />
      )
    case 'page-specific.invoice-banner':
      return (
        <InvoiceBanner
          {...(pageSpecificData as unknown as SectionsInvoiceBannerComponent)}
        />
      )
    case 'page-specific.cashback':
      return (
        <Cashback
          {...(pageSpecificData as unknown as SectionsCashbackComponent)}
        />
      )
    case 'page-specific.typeform':
      return (
        <Typeform
          {...(pageSpecificData as unknown as SectionsTypeformComponent)}
        />
      )
    case 'page-specific.contact-info':
      return (
        <ContactInfo
          {...(pageSpecificData as unknown as SectionsContactInfoComponent)}
        />
      )
    case 'page-specific.contact':
      return (
        <Contact
          {...(pageSpecificData as unknown as SectionsContactComponent)}
        />
      )
    case 'page-specific.contact-channels':
      return (
        <ContactChannels
          {...(pageSpecificData as unknown as SectionsContactChannelsComponent)}
        />
      )
    case 'page-specific.about-us-info':
      return (
        <AboutUsInfo
          {...(pageSpecificData as unknown as SectionsAboutUsInfoComponent)}
        />
      )
    case 'page-specific.board-profiles':
      return (
        <BoardProfileInfo
          {...(pageSpecificData as unknown as SectionsBoardProfilesComponent)}
        />
      )
    case 'page-specific.video-stories':
      return (
        <VideoStories
          {...(pageSpecificData as unknown as SectionsVideoStoriesComponent)}
        />
      )
    case 'page-specific.blog-list':
      return (
        <BlogList
          {...(pageSpecificData as unknown as SectionsBlogListComponent)}
        />
      )
    case 'page-specific.media-article-list':
      return (
        <MediaArticlesList
          {...(pageSpecificData as unknown as SectionsMediaArticleListComponent)}
        />
      )
    case 'page-specific.media-article-contact':
      return (
        <MediaArticleContact
          {...(pageSpecificData as unknown as SectionsMediaArticleContactComponent)}
        />
      )
    case 'page-specific.html-embed':
      return (
        <HTMLEmbed
          {...(pageSpecificData as unknown as SectionsHtmlEmbedComponent)}
        />
      )
    case 'page-specific.feature':
      return (
        <Feature
          {...(pageSpecificData as unknown as SectionsFeatureComponent)}
        />
      )
    default:
      return null
  }
}

export const PageSpecificComponents = ({ pageSpecific }: PageSpecificProps) => (
  <>
    {pageSpecific?.map((item, index) => (
      <PageSpecific
        key={`${item.__component}${index}`}
        pageSpecificData={item}
      />
    ))}
  </>
)
